// FeaturesHorizontal.js
import React from "react";
import {
  Brain,
  Sparkles,
  ScanLine,
  Gauge,
  ShieldCheck,
  SlidersHorizontal,
  Workflow,
  MessageSquareText,
  Plug,
  Bot,
  LineChart,
} from "lucide-react";

const FEATURES = [
  {
    key: "profiling",
    icon: Brain,
    title: "Deep data profiling, instantly",
    blurb:
      "Understand your data in minutes — completeness, patterns, anomalies, and structure across CRM, ERP, ATS, finance, and operational systems.",
  },
  {
    key: "migration-pipeline",
    icon: Workflow,
    title: "A structured path from source to target",
    blurb:
      "Source, extract, transform, validate, and reconcile — with an approval gate at every stage, so nothing loads to the target system until it's actually clean.",
  },
  {
    key: "ai-cleansing",
    icon: Sparkles,
    title: "AI-powered cleansing suggestions",
    blurb:
      "Surface and fix invalid formats, inconsistent values, and missing fields with smart, explainable recommendations you can apply in one click.",
  },
  {
    key: "deduplication",
    icon: ScanLine,
    title: "Smart deduplication across systems",
    blurb:
      "High-precision matching finds duplicate entities across sources — customers, suppliers, products, assets, candidates, and more.",
  },
  {
    key: "connectors",
    icon: Plug,
    title: "Connects to the systems you already run",
    blurb:
      "SQL Server, SharePoint, Oracle, Excel, and Dynamics 365 F&O out of the box — read live from source or land it in staging for review before it goes anywhere near production. Set a connection up once and reuse it across every project, instead of re-entering the same credentials for each new engagement.",
  },
  {
    key: "quality-score",
    icon: Gauge,
    title: "Data quality scores you can trust",
    blurb:
      "Track quality by dataset, source, or business area with clear scores for completeness, accuracy, consistency, and timeliness — then drill straight into the underlying rows in a live, filterable grid. Sort, group, and search thousands of records with friendly business names, right where the data lives.",
  },
  {
    key: "ai-assistant",
    icon: MessageSquareText,
    title: "Ask your data directly — skip the export to Excel",
    blurb:
      "Ask what's open, what changed, or what's still messy, and get a real answer grounded in your live project data — not a spreadsheet someone has to keep reconciling by hand. Have it build a status deck or email your team directly, in-app, or connect your own Claude Desktop or Claude Code straight to PrismDQ for the same live answers.",
  },
  {
    key: "ai-agent-access",
    icon: Bot,
    title: "Expose your data to AI agents, safely",
    blurb:
      "PrismDQ runs an MCP server alongside the app, so any MCP-compatible AI agent can query entities, quality issues, rules, and run history with a personal access token — the same permissions and audit trail as a signed-in user, nothing more.",
  },
  {
    key: "data-analysis",
    icon: LineChart,
    title: "Analyse trends, not just snapshots",
    blurb:
      "Quality and coverage scores over time, cross-source reconciliation, and drill-downs by dataset or business area — so you can see whether things are actually getting better, not just what today looks like.",
  },
  {
    key: "rules-engine",
    icon: SlidersHorizontal,
    title: "Your data rules, encoded in AI",
    blurb:
      "Capture business-specific rules — from validation and standardisation to retention and enrichment — so PrismDQ follows your playbook, not the other way around.",
  },
  {
    key: "privacy-security",
    icon: ShieldCheck,
    title: "Secure and private by design",
    blurb:
      "Role- and field-level access control, a full audit trail, and no use of your data to train public models.",
  },
];

export default function FeaturesHorizontal({
  title = "Built to fix data problems in any business.",
  subtitle =
    "PrismDQ profiles, scores, and cleans your data across CRM, ERP, ATS, finance, and operational systems, analyses it over time, runs the full migration from source to target when you're ready to move it, and makes it queryable by AI — in-app or through your own agents.",
  items = FEATURES,
}) {
  return (
    <section id="features" className="relative py-20">
      {/* ambient wash */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/2 top-0 h-64 w-[85vw] -translate-x-1/2 rounded-[8rem] bg-[radial-gradient(40rem_16rem_at_50%_0%,rgba(96,165,250,0.16),transparent)]" />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            {title}
          </h2>
        </div>
        <p className="mt-3 text-center text-slate-600">{subtitle}</p>

        {/* Feature grid */}
        <div className="relative mt-12">
          <div className="pointer-events-none absolute -inset-x-4 inset-y-0 -z-10 opacity-[0.04] bg-[linear-gradient(115deg,#000_1px,transparent_1px)] bg-[length:18px_18px]" />

          <ul className="grid grid-cols-1 gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
            {items.map(({ key, icon: Icon, title, blurb }) => (
              <li key={key} className="group relative">
                {/* icon chip */}
                <div className="mb-3 inline-flex items-center gap-2">
                  <span className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 text-white ring-1 ring-white/40 shadow transition-transform duration-300 group-hover:scale-105">
                    <Icon className="h-5 w-5" />
                  </span>
                </div>

                {/* text */}
                <h3 className="text-xl sm:text-2xl font-semibold leading-snug text-slate-900">
                  {title}
                </h3>
                <p className="mt-2 text-sm text-slate-600">{blurb}</p>

                {/* underline motif */}
                <div className="mt-4 h-1 w-16 rounded-full bg-gradient-to-r from-blue-600 via-sky-500 to-cyan-400 opacity-80 group-hover:opacity-100" />
              </li>
            ))}
          </ul>
        </div>

        {/* CTA */}
        <div className="mt-14 flex justify-center">
          <a
            href="https://app.prismdq.com/dashboard"
            className="rounded-full bg-gradient-to-r from-blue-600 via-sky-500 to-cyan-400 px-8 py-3 text-lg font-semibold text-white shadow-lg backdrop-blur hover:opacity-90 transition"
          >
            Sign Up
          </a>
        </div>
      </div>
    </section>
  );
}